// The pure half of the Vorlagen-Übersicht graph (template_graph_controller.js): turns the JSON of
// Timesheet::TemplateGraphBuilder plus the active filters into Cytoscape elements.
//
// Employees are never drawn one node per person. Their edges are bundled by the builder's
// `group` key (same source, kind, path and template), and each group becomes either
//   - a count node ("41 Mitarbeiter"), one edge from its source, or
//   - when expanded, a block: a frame sized for a grid of name chips ("member" nodes).
// A tenant with 900 employees on one template otherwise renders as a single column thousands
// of pixels tall that no zoom level can make readable.
//
// An employee belonging to several groups (their default via a trade, excluded from another
// template) gets one chip in every expanded group; the chips share `employee_id`.

// At most this many employees in view (a small tenant, or a search / filter narrowing a large
// one down) and every group is drawn expanded — the graph then reads like a plain
// employee graph. Above it every group starts as a count node.
export const REVEAL_LIMIT = 60
// The employees who have no template at all.
export const WITHOUT_GROUP = "without"

export const CELL = { width: 168, height: 30 }
const CHIP = { width: CELL.width - 8, height: CELL.height - 6 }
export const BLOCK_PADDING = 12
export const BLOCK_HEADER = 28
export const PILL_HEIGHT = 26
const PILL_CHAR_WIDTH = 6.8
const PILL_WIDTH = { min: 72, max: 260 }

// { nodesById, employees, incoming: employee id -> employee edges, structure: "source|template" -> edge }
export function indexGraph({ nodes, edges }) {
  const nodesById = new Map(nodes.map(({ data }) => [data.id, data]))
  const incoming = new Map()
  const structure = new Map()
  edges.forEach(({ data }) => {
    if (data.kind === "structure") {
      structure.set(`${data.target}|${data.template_id}`, data)
      return
    }
    if (!incoming.has(data.target)) incoming.set(data.target, [])
    incoming.get(data.target).push(data)
  })
  const employees = nodes.map(({ data }) => data).filter((data) => data.type === "employee")
  return { nodesById, employees, incoming, structure }
}

export function templates(index) {
  return [...index.nodesById.values()].filter((node) => node.type === "template")
}

// The employees the filters keep, each with the edges still shown for them. Same rules as the
// list: a template filter keeps only employees reaching that template, a search matches the
// employee's name or any template / trade / role on their way to a template.
export function selectEmployees(
  index,
  { templateId = "", showAvailable = false, onlyWithout = false, query = "" },
) {
  const needle = query.trim().toLowerCase()
  return index.employees.flatMap((employee) => {
    if (onlyWithout && !employee.without_template) return []
    const edges = (index.incoming.get(employee.id) || []).filter((edge) =>
      edgeShown(edge, templateId, showAvailable),
    )
    if (templateId && edges.length === 0) return []
    if (needle && !chainMatches(index, employee, edges, needle)) return []
    return [{ employee, edges }]
  })
}

function edgeShown(edge, templateId, showAvailable) {
  if (templateId && String(edge.template_id) !== templateId) return false
  return showAvailable || !edge.via_all
}

function chainMatches(index, employee, edges, needle) {
  if (employee.label.toLowerCase().includes(needle)) return true
  return edges.some((edge) =>
    chainNodeIds(index, edge).some((id) =>
      index.nodesById.get(id)?.label.toLowerCase().includes(needle),
    ),
  )
}

// The source of an employee edge and, for a trade or role, the template above it.
function chainNodeIds(index, edge) {
  const up = index.structure.get(`${edge.source}|${edge.template_id}`)
  return up ? [edge.source, up.source] : [edge.source]
}

// Map group key -> { key, source, kind, via, template_id, members }, in first-seen order.
// Every employee without any template is also in WITHOUT_GROUP (no source): their only edges
// are exclusions, and folded into those groups nobody would notice they lack a template.
export function buildGroups(selection) {
  const groups = new Map()
  const add = (key, edge, employee) => {
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        source: edge?.source ?? null,
        kind: edge?.kind ?? "without",
        via: edge?.via ?? null,
        template_id: edge?.template_id ?? null,
        members: [],
      })
    }
    groups.get(key).members.push(employee)
  }
  selection.forEach(({ employee, edges }) => {
    if (employee.without_template) add(WITHOUT_GROUP, null, employee)
    edges.forEach((edge) => add(edge.group, edge, employee))
  })
  return groups
}

// The groups open by default: every group when few employees are in view, none otherwise.
// "Nur ohne Vorlage" opens only the without group — the same people fill each exclusion
// group, and drawing them five times over buries the one list that matters.
export function defaultOpenKeys(groups, { reveal = false, onlyWithout = false } = {}) {
  if (!reveal) return new Set()
  return new Set([...groups.keys()].filter((key) => !onlyWithout || key === WITHOUT_GROUP))
}

// The keys drawn expanded. `toggled` holds the groups the user flipped away from their default.
export function expandedKeys(groups, toggled, defaults) {
  return new Set([...groups.keys()].filter((key) => defaults.has(key) !== toggled.has(key)))
}

// Roughly square in pixels: chips are wide and flat, so a block gets fewer columns than rows.
export function gridSize(count) {
  const cols = Math.max(1, Math.round(Math.sqrt((count * CELL.height) / CELL.width)))
  const rows = Math.max(1, Math.ceil(count / cols))
  return {
    cols,
    rows,
    width: cols * CELL.width + 2 * BLOCK_PADDING,
    height: rows * CELL.height + BLOCK_HEADER + BLOCK_PADDING,
  }
}

// Centre of chip `index` inside a block centred on `center`.
export function memberPosition(index, size, center) {
  const left = center.x - size.width / 2 + BLOCK_PADDING
  const top = center.y - size.height / 2 + BLOCK_HEADER
  return {
    x: left + (index % size.cols) * CELL.width + CELL.width / 2,
    y: top + Math.floor(index / size.cols) * CELL.height + CELL.height / 2,
  }
}

// Every node but a chip and an expanded block is a pill sized to its label, so the label is
// part of the node and dagre spaces it, instead of hanging below a dot into its neighbours.
export function pillWidth(label) {
  return Math.min(Math.max(label.length * PILL_CHAR_WIDTH + 24, PILL_WIDTH.min), PILL_WIDTH.max)
}

export function edgeWidth(count) {
  return Math.min(1.5 + Math.log2(count) * 1.2, 9)
}

// labels: { employees: { one, other }, without: { one, other }, via: { pin, all, exclusion } }
// with %{count} placeholders, from the view's I18n.
export function groupLabel(group, labels) {
  const count = group.members.length
  const plural = (forms) => (count === 1 ? forms.one : forms.other).replace("%{count}", count)
  if (group.key === WITHOUT_GROUP) return plural(labels.without)
  const text = plural(labels.employees)
  // Behind a trade or role the source already says why; straight from a template it doesn't.
  const reason = group.source?.startsWith("template-") ? labels.via[group.via] : null
  return reason ? `${text} · ${reason}` : text
}

// Everything Cytoscape draws for the current filters: templates, the trades / roles and
// structure edges that lead to a visible group, group nodes and their edges, and a chip per
// member of an expanded group. Member chips carry no position; the controller places them
// in their block after the layout.
export function buildElements(index, filters, toggled, labels) {
  const selection = selectEmployees(index, filters)
  const groups = buildGroups(selection)
  const reveal = selection.length <= REVEAL_LIMIT
  const defaults = defaultOpenKeys(groups, { reveal, onlyWithout: filters.onlyWithout })
  const open = expandedKeys(groups, toggled, defaults)
  const needle = (filters.query || "").trim().toLowerCase()

  const nodes = new Map()
  const edges = new Map()
  const addNode = (data) => nodes.set(data.id, { data })
  const addPill = (data) => addNode({ ...data, w: pillWidth(data.label), h: PILL_HEIGHT })
  // A structure edge is as thick as the groups it feeds together.
  const addSource = (id, templateId, count) => {
    addPill(index.nodesById.get(id))
    const up = index.structure.get(`${id}|${templateId}`)
    if (!up) return
    addPill(index.nodesById.get(up.source))
    const total = (edges.get(up.id)?.data.count ?? 0) + count
    edges.set(up.id, { data: { ...up, count: total, width: edgeWidth(total) } })
  }

  // Templates stand on their own so an unused one still shows — except under "Nur ohne
  // Vorlage", where only the templates those employees are excluded from belong.
  templates(index).forEach((template) => {
    if (filters.onlyWithout) return
    if (filters.templateId && String(template.record_id) !== filters.templateId) return
    if (needle && !template.label.toLowerCase().includes(needle)) return
    addPill(template)
  })

  const members = []
  groups.forEach((group) => {
    const id = `group:${group.key}`
    const count = group.members.length
    const label = groupLabel(group, labels)
    const isOpen = open.has(group.key)
    const size = isOpen ? gridSize(count) : { width: pillWidth(label), height: PILL_HEIGHT }
    addNode({
      id,
      type: "group",
      group: group.key,
      kind: group.kind,
      count,
      label,
      expanded: isOpen,
      w: size.width,
      h: size.height,
      cols: size.cols,
      template_id: group.template_id,
    })
    if (group.source) {
      addSource(group.source, group.template_id, count)
      edges.set(`${group.source}->${id}`, {
        data: {
          id: `${group.source}->${id}`,
          source: group.source,
          target: id,
          kind: group.kind,
          count,
          width: edgeWidth(count),
          template_id: group.template_id,
        },
      })
    }
    if (!isOpen) return
    group.members.forEach((employee, position) => {
      members.push({
        data: {
          id: `member:${group.key}:${employee.id}`,
          type: "member",
          group: id,
          position,
          w: CHIP.width,
          h: CHIP.height,
          employee_id: employee.record_id,
          label: employee.label,
          without_template: employee.without_template,
        },
      })
    })
  })

  return {
    elements: [...nodes.values(), ...edges.values(), ...members],
    groups,
    defaults,
    selection,
  }
}
